import { CASE_H, FLOOR_H, STANDS_AT } from '../level.js';
import { sizedSprite, stripStyle } from '../print.jsx';
import { READER, TORSO_REACH, castReader, compile, strip } from '../sprites.js';
import './spooky.css';

// Spooky season: the last week of September to Hallowe'en.
//
// The reader is Frankenstein's monster for the month, and the room has spiders
// in it, cobwebs in its top corners, a candle in a sconce in every gap
// between two bookcases, a black cat, a jack-o'-lantern, the odd bat and a ghost.
// Every colour below is off the console's chart, like everything else in the
// level - see palette.js.

// --- the monster -------------------------------------------------------------

// The same 24 wide and 16 tall as the reader's own head, so the torsos and legs
// fit under it unchanged. A flat top with a shine along it, a fringe, stitches
// across the forehead, a brow you could stand a book on, and the bolts - which
// are on his neck, under the jaw, where the film put them.
const MONSTER_HEAD = [
  '....kkkkkkkkkkkkkkkk....',
  '...kHHHHHHHHHHHHHHHHk...',
  '...khhhhhhhhhhhhhhhhk...',
  '...khhhhhhhhhhhhhhhhk...',
  '...khhhhhhhhhhhhhhhhk...',
  '...khshhshhshhshhshhk...',
  '...kssssssssssssssssk...',
  '...ksksksksksssssssSk...',
  '...kSkkkkkSSSSkkkkkSk...',
  '...kssskksssssskksssk...',
  '...kssskksssssskksssk...',
  '...kssssssssssssssssk...',
  '...ksssskkkkkkkkssssk...',
  '...kSssssssssssssssSk...',
  '....kkkkksssssskkkkk....',
  '......nnkSSSSSSknn......',
];

const MONSTER_KEY = {
  k: '#1c1c1c',
  h: '#1c1c1c', // hair, which is the outline's black - the shine is what draws the top
  H: '#7c7c7c',
  s: '#58d854', // skin
  S: '#00a800', // and its shadow
  n: '#bcbcbc', // bolts
  t: '#7c7c7c', // a jacket that does not fit
  u: '#1c1c1c',
  // Trousers. The reader's own grey, and for his reason: the floor is behind
  // them, and anything darker measures nothing against it.
  p: '#bcbcbc',
  b: '#f8b800', // the book he carries, unchanged - it is the same book
  w: '#f8f8f8',
};

// He walks with his arms out in front of him. It is the one thing everybody
// knows about how he moves, and the reaching torso already has an arm out.
const MONSTER = castReader({ head: MONSTER_HEAD, key: MONSTER_KEY, walk: TORSO_REACH });

// --- the room ----------------------------------------------------------------

// A spider from above, head up, in two frames of its legs. Black with a red
// hourglass on its back, and grey legs - a black spider on a wall this dark is
// not there at all, and the legs are what read as a spider anyway.
const SPIDER_KEY = { l: '#7c7c7c', k: '#1c1c1c', r: '#f83800' };
const SPIDER = strip([
  [
    '.l.....l.',
    '..l...l..',
    'l..rkr..l',
    '.lllklll.',
    '...krk...',
    '.lllklll.',
    'l..kkk..l',
    '..l...l..',
    '.l.....l.',
  ],
  [
    'l.......l',
    '.l.....l.',
    '.l.rkr.l.',
    'llllkllll',
    '...krk...',
    'llllkllll',
    '.l.kkk.l.',
    '.l.....l.',
    'l.......l',
  ],
], SPIDER_KEY);

const THREAD = '#bcbcbc';

// The top-left corner's web. The right-hand one is drawn separately, below,
// and flipped.
const COBWEB = compile([
  'ww...w.....w.....W',
  'wwww.w.....w.....W',
  '.ww.ww....w.....W.',
  '.w.ww.ww..w.....W.',
  '..www...ww.....W..',
  'www..w...www...W..',
  '...w..w.w...wwW...',
  '...w...w......Ww..',
  '....w.w.w....W..ww',
  '....ww...w..W.....',
  '..ww.w....wW......',
  'ww...w....Ww......',
  '......w..W..w.....',
  '......w.W....w....',
  '......WW......w...',
  '....WW.w.......w..',
  '..WW....w.......w.',
  'WW......w........w',
], { w: '#bcbcbc', W: '#7c7c7c' });

// The right-hand corner's, which is a quarter as big again as the one above -
// or would be, at 22.5 pixels. A sprite scaled by a fraction lands half its
// pixels between two of the screen's and smears them grey, so this is the
// same web drawn again at 23: the same three strands out of the corner and
// the same three rings, the outer one fainter, at the radii scaled up.
const COBWEB_RIGHT = compile([
  'w......w......w.......W',
  '.ww....w......w.......W',
  '.wwww.w......w.......W.',
  '..ww.ww......w.......W.',
  '..w.ww.ww...w.......W..',
  '...www...ww.w.......W..',
  '..ww..w....ww......W...',
  'ww..w..w..w..ww....W...',
  '....w...w.w....ww.W....',
  '.....w...w.......wW....',
  '.....w.ww.w......W.ww..',
  '......w....w....W....ww',
  '....www.....w...W......',
  '..ww...w.....w.W.......',
  'ww.....w......W........',
  '........w....W.w.......',
  '........w..WW...w......',
  '.........wW......w.....',
  '........WW........w....',
  '......WW..w........w...',
  '....WW....w.........w..',
  '..WW.......w.........w.',
  'WW.........w..........w',
], { w: '#bcbcbc', W: '#7c7c7c' });

// How much bigger than its drawing each thing is drawn. Whole numbers only,
// for the reason COBWEB_RIGHT is a drawing of its own.
const LEFT_WEB_ZOOM = 2;
const SPIDER_ZOOM = 2;

// A black cat, sitting, in the potted plant's place - the same eleven pixels
// wide so it stands where the plant did. Black on a wall this dark is not
// there at all, so the light catches its ears, its back and the tip of its
// tail in grey; the eyes are the brightest green the console had.
const BLACK_CAT = compile([
  '..R...R....',
  '..kR.Rk....',
  '..kkkkk....',
  '..kGkGk....',
  '..kkkkk....',
  '...kkk.....',
  '..Rkkkk....',
  '.Rkkkkkk...',
  '.kkkkkkk...',
  '.kkkkkkk..R',
  '.kkkkkkk..k',
  '.kkkkkkk.kk',
  '.kkkkkkkkk.',
  '.kk.k.kk...',
], { k: '#1c1c1c', R: '#7c7c7c', G: '#b8f818' });

// A jack-o'-lantern in the crate's place, the crate's ten pixels wide. Ribbed
// in the darker orange, lit from inside in the palest yellow the chart has -
// the face is the brightest thing on the floor, which is the point of one.
const JACK_O_LANTERN = compile([
  '....gg....',
  '.....g....',
  '.oOOoOOOo.',
  'oOOOoOOOOo',
  'oOyOoOOyOo',
  'oyyyOOyyyo',
  'oOOOyyOOOo',
  'oyOyyyyOyo',
  'oOyyOOyyOo',
  '.oOOOOOOo.',
], { g: '#006800', o: '#881400', O: '#e45c10', y: '#f8d878' });

// A bat, wings up and wings down. Grey wings for the spider's reason, and the
// body two pixels lower in the upstroke, so it bobs as it flaps.
const BAT = strip([
  [
    'll...........ll',
    'lll.........lll',
    '.lll..k.k..lll.',
    '..lllkkkkklll..',
    '....lkrkrkl....',
    '......kkk......',
    '.......k.......',
  ],
  [
    '......k.k......',
    '.....kkkkk.....',
    '...llkrkrkll...',
    '.lllllkkklllll.',
    'llll...k...llll',
    'll...........ll',
    'l.............l',
  ],
], { l: '#7c7c7c', k: '#1c1c1c', r: '#f83800' });

const BAT_ZOOM = 2;

// The two bats: how far below the status bar each crosses, how fast, and how
// long a cycle is - as a multiple of the crossing, with a floor in seconds.
// The crossing is the first fifth of the cycle (see eb-bat), so on a narrow
// portrait screen, where it is short, the multiple alone brought a bat round
// every twenty seconds; the floor keeps it occasional, and the bat flies a
// little slower to fill it. Out of step with each other, so the screen gets
// one now and then rather than a pair on a timetable.
const BATS = [
  { below: 18, speed: 64, every: 5, atLeast: 35, offset: 0.35 },
  { below: 52, speed: 46, every: 7, atLeast: 50, offset: 0.8 },
];

// Leave every other pixel of `body` empty, in a checkerboard. The console
// could not draw anything half transparent, and the games that wanted a ghost
// did this: the room shows through the holes, and at a few feet the eye mixes
// the two. It keeps every pixel whole and every colour on the chart, where an
// opacity would blend the ghost into colours the console never had. The art
// below is drawn solid, so it stays editable; the holes are made here.
const dither = (rows, body) => rows.map((row, y) =>
  [...row].map((ch, x) => (ch === body && (x + y) % 2 ? '.' : ch)).join(''));

// A ghost, facing right, the way it drifts. A solid outline and solid eyes,
// so its shape holds wherever it is, and a body the reader shows through.
const GHOST = compile(dither([
  '....wwwwww....',
  '..wwWWWWWWww..',
  '.wWWWWWWWWWWw.',
  '.wWWWWWWWWWWw.',
  'wWWWWWkkWWkkWw',
  'wWWWWWkkWWkkWw',
  'wWWWWWWWWWWWWw',
  'wWWWWWWWkkWWWw',
  'wWWWWWWWkkWWWw',
  'wWWWWWWWWWWWWw',
  'wWWWWWWWWWWWWw',
  'wWWWWWWWWWWWWw',
  'wWWWWWWWWWWWWw',
  'wWWwWWWwWWWwWw',
  'wWw.wWw.wWw.ww',
  '.w...w...w...w',
], 'W'), { w: '#f8f8f8', W: '#bcbcbc', k: '#1c1c1c' });

const GHOST_ZOOM = 2;

// Its crossing: at the height of the reader's chest, so it goes through him,
// slower than a bat and less often. The camera keeps him on the screen, so a
// crossing of the whole screen always meets him - unless he is up a ladder,
// and then it passes under his boots, which is fine.
const GHOST_FLIGHT = {
  chest: STANDS_AT + Math.round(READER.idle.height * 0.3),
  speed: 30,
  every: 5,
  atLeast: 60,
  offset: 0.55,
};

// A candle on a brass sconce, with two frames of flame.
const SCONCE_KEY = {
  Y: '#fca044',
  y: '#f8d878',
  o: '#e45c10',
  w: '#f8f8f8',
  W: '#bcbcbc',
  B: '#f8b800',
  b: '#ac7c00',
  k: '#1c1c1c',
};
const SCONCE_BODY = [
  '..www..',
  '..wwW..',
  '..wwW..',
  '..wwW..',
  '..wwW..',
  'kBBBBBk',
  '.kBbBk.',
  '..kBk..',
  '...B...',
  '..BbB..',
  '..BbB..',
  '...b...',
];
const SCONCE = strip([
  ['...Y...', '..YyY..', '..YyY..', '...o...', ...SCONCE_BODY],
  ['..Y....', '..YY...', '..YyY..', '...o...', ...SCONCE_BODY],
], SCONCE_KEY);

// How high a sconce hangs, above the floor the bookcases stand on: well clear
// of the tallest prop under it, which is the lamp, and below a bookcase's top
// so it is among the furniture rather than up by the signs.
const SCONCE_AT = 84;

// A spider as drawn on the level.
const SPIDER_W = SPIDER.width * SPIDER_ZOOM;
const SPIDER_H = SPIDER.height * SPIDER_ZOOM;
// The column its body is centred on, which at an even zoom is two pixels wide
// - so the thread it hangs from is too, and lines up with the body rather than
// hanging off one side of it.
const SPIDER_MID = ((SPIDER.width - 1) / 2) * SPIDER_ZOOM;

// Spiders are placed by bookcase number rather than at random, so a display
// that reloads comes back to the same room - the same rule the props follow.
// The first bookcase always has one, so a small library is not spared.
const DANGLE_EVERY = 3;
const CLIMB_EVERY = 4;
const CLIMB_ON = 2;

function Room({ level, ceiling }) {
  const floor = FLOOR_H - 1; // where the bookcases stand, up from the stage's foot
  const out = [];

  level.gaps.forEach((gap, i) => {
    out.push(
      <i
        key={`sconce-${i}`}
        className="eb-sconce"
        style={{
          left: `${Math.round(gap.from + (gap.to - gap.from - SCONCE.width) / 2)}px`,
          bottom: `${floor + SCONCE_AT}px`,
          ...stripStyle(SCONCE),
          // Out of step with each other, or a room of candles flickers as one.
          animationDuration: `${400 + ((i * 7) % 5) * 60}ms`,
          animationDelay: `-${(i * 130) % 500}ms`,
        }}
      />
    );
  });

  level.cases.forEach((entry, i) => {
    if (i % DANGLE_EVERY === 0) {
      // Down out of the ceiling on a thread, a while hanging there, and back
      // up into the dark under the status bar. How far below the ceiling it
      // stops is in front of the signs and the top shelf on a television, and
      // among the pictures on a tall wall.
      const below = 16 + ((i * 13) % 40);
      const reach = ceiling + below;
      const drop = below + SPIDER_H;
      const seconds = 16 + ((i * 7) % 9);
      out.push(
        <span
          key={`dangle-${i}`}
          className="eb-dangler"
          style={{
            left: `${entry.x + 8 + ((i * 29) % 50)}px`,
            width: `${SPIDER_W}px`,
            height: `${reach + SPIDER_H}px`,
            background: `linear-gradient(${THREAD}, ${THREAD}) ${SPIDER_MID}px 0 / ${SPIDER_ZOOM}px ${reach}px no-repeat`,
            '--eb-drop': drop,
            animationDuration: `${seconds}s`,
            animationTimingFunction: `steps(${drop}, end)`,
            animationDelay: `-${(i * 5) % seconds}s`,
          }}
        >
          <i className="eb-spider" style={stripStyle(SPIDER, SPIDER_ZOOM)} />
        </span>
      );
    }

    if (i % CLIMB_EVERY === CLIMB_ON) {
      // Up the front of a bookcase's left-hand side and back down it, turning
      // round at each end.
      const travel = CASE_H - 30 - SPIDER_H;
      const seconds = 18 + ((i * 5) % 8);
      out.push(
        <span
          key={`climb-${i}`}
          className="eb-climber"
          style={{
            // Its body over the middle of the three-pixel frame.
            left: `${entry.x + 1 - SPIDER_MID}px`,
            bottom: `${floor + 8}px`,
            width: `${SPIDER_W}px`,
            height: `${SPIDER_H}px`,
            '--eb-travel': travel,
            animationTimingFunction: `steps(${travel}, end)`,
            '--eb-cycle': `${seconds}s`,
            '--eb-offset': `-${(i * 3) % seconds}s`,
          }}
        >
          <i className="eb-spider" style={stripStyle(SPIDER, SPIDER_ZOOM)} />
        </span>
      );
    }
  });

  return out;
}

// A ghost drifting across the screen from left to right, at the reader's
// height, with a slow bob. Its own layer is the screen's, which is drawn over
// the reader, so it passes in front of him - and he shows through it.
function Ghost({ worldW, worldH }) {
  const w = GHOST.width * GHOST_ZOOM;
  const h = GHOST.height * GHOST_ZOOM;
  const distance = worldW + w;
  const flight = GHOST_FLIGHT;
  const cycle = Math.max(flight.atLeast, (distance / flight.speed) * flight.every);
  return (
    <span
      className="eb-ghost"
      style={{
        top: `${worldH - flight.chest - h}px`,
        left: 0,
        width: `${w}px`,
        height: `${h}px`,
        '--eb-from': -w,
        '--eb-to': worldW,
        animationDuration: `${cycle.toFixed(3)}s`,
        animationDelay: `-${(cycle * flight.offset).toFixed(3)}s`,
        animationTimingFunction: `steps(${distance}, end)`,
      }}
    >
      <i className="eb-ghost-body" style={sizedSprite(GHOST, GHOST_ZOOM)} />
    </span>
  );
}

function Screen({ worldW, worldH, ceiling }) {
  const w = BAT.width * BAT_ZOOM;
  // In from just past the right-hand edge, out just past the left, one whole
  // pixel at a time.
  const distance = worldW + w;
  return (
    <>
      {BATS.map((bat, i) => {
        const cycle = Math.max(bat.atLeast, (distance / bat.speed) * bat.every);
        return (
          <span
            key={`bat-${i}`}
            className="eb-bat"
            style={{
              top: `${ceiling + bat.below}px`,
              left: 0,
              width: `${w}px`,
              height: `${BAT.height * BAT_ZOOM}px`,
              '--eb-from': worldW,
              '--eb-to': -w,
              animationDuration: `${cycle.toFixed(3)}s`,
              animationDelay: `-${(cycle * bat.offset).toFixed(3)}s`,
              animationTimingFunction: `steps(${distance}, end)`,
            }}
          >
            <i className="eb-bat-wings" style={stripStyle(BAT, BAT_ZOOM)} />
          </span>
        );
      })}
      <Ghost worldW={worldW} worldH={worldH} />
      <i className="eb-cobweb" style={{ top: `${ceiling}px`, left: 0, ...sizedSprite(COBWEB, LEFT_WEB_ZOOM) }} />
      <i
        className="eb-cobweb eb-cobweb--right"
        style={{ top: `${ceiling}px`, right: 0, ...sizedSprite(COBWEB_RIGHT) }}
      />
    </>
  );
}

export default {
  id: 'spooky',
  from: [9, 24],
  to: [10, 31],
  reader: MONSTER,
  props: { plant: BLACK_CAT, crate: JACK_O_LANTERN },
  Room,
  Screen,
};
